import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { RevenueData } from "@shared/types";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface RevenueChartProps {
  locationId: string;
  organizationId?: string;
}

export function RevenueChart({ locationId, organizationId }: RevenueChartProps) {
  const [timeFrame, setTimeFrame] = useState<"daily" | "weekly" | "monthly">("daily");

  // Build API URL with organization parameter for multi-organization support
  const buildApiUrl = () => {
    let url = `/api/metrics-chart?locationId=${locationId}&category=revenue&timeFrame=${timeFrame}`;
    if (organizationId) {
      url += `&organizationId=${organizationId}`;
    }
    return url;
  };

  // This would be a real API call in production
  const { data: revenueData, isLoading } = useQuery<RevenueData[]>({
    queryKey: [buildApiUrl(), organizationId],
  });

  const data = revenueData || [];

  const getAverageRevenue = () => {
    if (!data || data.length === 0) return "0";
    const sum = data.reduce((acc, entry) => acc + entry.value, 0);
    return (sum / data.length).toFixed(0);
  };

  const getHighestRevenue = () => {
    if (!data || data.length === 0) return "0";
    return Math.max(...data.map(entry => entry.value)).toFixed(0);
  };

  const getLowestRevenue = () => {
    if (!data || data.length === 0) return "0";
    return Math.min(...data.map(entry => entry.value)).toFixed(0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Card className="p-4">
      <CardHeader className="p-0 pb-4">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg font-medium">Revenue Trends</CardTitle>
          <div className="flex space-x-2">
            <Button
              variant={timeFrame === "daily" ? "secondary" : "outline"}
              size="sm"
              className="text-xs px-3 py-1 h-auto"
              onClick={() => setTimeFrame("daily")}
            >
              Daily
            </Button>
            <Button
              variant={timeFrame === "weekly" ? "secondary" : "outline"}
              size="sm"
              className="text-xs px-3 py-1 h-auto"
              onClick={() => setTimeFrame("weekly")}
            >
              Weekly
            </Button>
            <Button
              variant={timeFrame === "monthly" ? "secondary" : "outline"}
              size="sm"
              className="text-xs px-3 py-1 h-auto"
              onClick={() => setTimeFrame("monthly")}
            >
              Monthly
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : data.length === 0 ? <p className="py-8 text-sm text-slate-600 dark:text-slate-300">No revenue data for this period.</p> : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976D2" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#1976D2" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis 
                  tickFormatter={(value) => `$${value/1000}k`} 
                  tick={{ fontSize: 12 }} 
                />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value as number), "Revenue"]} 
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#1976D2"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-neutral-400">Avg. Daily</p>
            <p className="font-medium">{data.length ? formatCurrency(parseInt(getAverageRevenue())) : "—"}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-neutral-400">Highest Day</p>
            <p className="font-medium">{data.length ? formatCurrency(parseInt(getHighestRevenue())) : "—"}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-neutral-400">Lowest Day</p>
            <p className="font-medium">{data.length ? formatCurrency(parseInt(getLowestRevenue())) : "—"}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
